import { inject, Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { WorkOrderService } from '../data-services/work-order.service';
import { ToastService } from './toast.service';
import { WebSocketService } from './websocket.service';

/**
 * Hub-emitted payload for `work-order.created` and `work-order.updated`.
 * Lean — clients fetch the full row by id when needed.
 */
export interface WorkOrderEventPayload {
  workOrderId: string;
  organizationId: string;
  jobId?: string | null;
  assignedPilotProfileId?: string | null;
  status?: string;
  actorProfileId?: string | null;
  assignedByName?: string | null;
  /**
   * socket.io connection id of the client that initiated the
   * mutation. Receivers compare it against their own socket id and
   * suppress only their own writes.
   */
  originSocketId?: string | null;
  timestamp: string;
}

export interface WorkOrderDeletedPayload {
  workOrderId: string;
  organizationId: string;
  actorProfileId?: string | null;
  originSocketId?: string | null;
  timestamp: string;
}

/**
 * Work Order Real-time Service
 *
 * Listens for `work-order.{created,updated,deleted}` on the org room.
 * Re-syncs the local cache so any open list / detail view reflects the
 * change immediately. `originSocketId` suppression mirrors the
 * spray-record realtime service — the originating tab stays quiet
 * while other tabs and the assigned pilot's mobile still get toasted.
 */
@Injectable({ providedIn: 'root' })
export class WorkOrderRealtimeService implements OnDestroy {
  private destroy$ = new Subject<void>();

  private createdSubject = new BehaviorSubject<WorkOrderEventPayload | null>(null);
  public created$: Observable<WorkOrderEventPayload | null> = this.createdSubject.asObservable();

  private updatedSubject = new BehaviorSubject<WorkOrderEventPayload | null>(null);
  public updated$: Observable<WorkOrderEventPayload | null> = this.updatedSubject.asObservable();

  private deletedSubject = new BehaviorSubject<WorkOrderDeletedPayload | null>(null);
  public deleted$: Observable<WorkOrderDeletedPayload | null> = this.deletedSubject.asObservable();

  private isSubscribed = false;

  private websocketService = inject(WebSocketService);
  private workOrderService = inject(WorkOrderService);
  private toastService = inject(ToastService);
  private router = inject(Router);

  constructor() {
    this.websocketService.connectionState$
      .pipe(takeUntil(this.destroy$))
      .subscribe((state) => {
        if (state === 'authenticated' && !this.isSubscribed) {
          this.subscribeToEvents();
        } else if (state === 'disconnected') {
          this.isSubscribed = false;
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private subscribeToEvents(): void {
    if (this.isSubscribed) return;
    this.isSubscribed = true;

    this.websocketService
      .on<WorkOrderEventPayload>('work-order.created')
      .pipe(takeUntil(this.destroy$))
      .subscribe((event) => this.handleCreated(event));

    this.websocketService
      .on<WorkOrderEventPayload>('work-order.updated')
      .pipe(takeUntil(this.destroy$))
      .subscribe((event) => this.handleUpdated(event));

    this.websocketService
      .on<WorkOrderDeletedPayload>('work-order.deleted')
      .pipe(takeUntil(this.destroy$))
      .subscribe((event) => this.handleDeleted(event));
  }

  private async handleCreated(event: WorkOrderEventPayload): Promise<void> {
    console.log('[WorkOrderRealtime] Received work-order.created:', event);
    this.createdSubject.next(event);
    // Pull the full row to populate the local cache (covers list + any
    // open detail screen). Cheap roundtrip; payload is lean by design.
    await this.workOrderService.fetchOne(event.workOrderId);
    if (this.isSelfOrigin(event.originSocketId)) return;
    this.toastService.show('A new work order was created.', {
      type: 'info',
      title: 'Work Order Created',
      icon: 'bi-clipboard-plus',
      action: {
        label: 'View',
        callback: () => this.router.navigate(['/work-orders', event.workOrderId]),
      },
    });
  }

  private async handleUpdated(event: WorkOrderEventPayload): Promise<void> {
    console.log('[WorkOrderRealtime] Received work-order.updated:', event);
    this.updatedSubject.next(event);
    await this.workOrderService.fetchOne(event.workOrderId);
    if (this.isSelfOrigin(event.originSocketId)) return;
    this.toastService.show('A work order was updated.', {
      type: 'info',
      title: 'Work Order Updated',
      icon: 'bi-arrow-repeat',
      action: {
        label: 'View',
        callback: () => this.router.navigate(['/work-orders', event.workOrderId]),
      },
    });
  }

  private handleDeleted(event: WorkOrderDeletedPayload): void {
    console.log('[WorkOrderRealtime] Received work-order.deleted:', event);
    this.deletedSubject.next(event);
    this.workOrderService.removeFromCache(event.workOrderId);
    if (this.isSelfOrigin(event.originSocketId)) return;
    this.toastService.show('A work order was deleted.', {
      type: 'warning',
      title: 'Work Order Removed',
      icon: 'bi-trash',
    });
  }

  /**
   * True when this tab itself initiated the change. Same pattern as
   * the spray-record realtime service: the Hub stamps the originating
   * client's `socket.id` on every event payload (via
   * `X-Client-Socket-Id`). A match means "we did this" — the user
   * doesn't need a self-toast about their own action. The same user
   * signed in elsewhere has a different socket id and still gets the
   * toast. The cache refresh runs regardless.
   */
  private isSelfOrigin(originSocketId: string | null | undefined): boolean {
    if (!originSocketId) return false;
    const mine = this.websocketService.getSocketId();
    return !!mine && mine === originSocketId;
  }
}
